import { type ReactNode, useState } from 'react';
import { isExperimentApprovalsEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { Experiment } from '@gonfalon/experiments/types';
import { Alert, Button, Heading, IconButton, Label, Text, TextArea } from '@launchpad-ui/components';
// oxlint-disable-next-line no-restricted-imports
import { RequiredAsterisk } from '@launchpad-ui/core';

import { ExperimentPendingApprovalAlerts } from '#components/experimentation/common/components/ExperimentApprovalModal/ExperimentPendingApprovalAlerts';
import { ExperimentEnvironmentConfirmation } from '#components/experimentation/common/components/ExperimentEnvironmentConfirmation/ExperimentEnvironmentConfirmation';
import footerStyles from '#components/experimentation/common/components/TwoStepModalFooter.module.css';

import styles from './SaveExperimentModal.module.css';

type ExperimentIterationStatus = NonNullable<Experiment['currentIteration']>['status'];

type SaveExperimentModalProps = {
  onCancel: () => void;
  onConfirm: (params: { reason: string }) => void;
  isPending: boolean;
  hasStratifiedSampling?: boolean;
  /** Optional rich description rendered above the reason textarea. */
  description?: ReactNode;
  /** Overrides the primary button label (default: "Save changes and start new iteration"). */
  confirmLabel?: string;
  /** Optional step indicator rendered at the start of the footer (e.g. "Step 1/2"). */
  stepIndicator?: ReactNode;
  /** Pre-fills the reason textarea (e.g. when navigating back from a later step). */
  initialReason?: string;
  hasPendingApproval?: boolean;
  experimentCurrentIterationStatus?: ExperimentIterationStatus;
  /**
   * Require typing the environment key/name to confirm before saving. Defaults to `true`.
   * Set to `false` only when confirmation is collected on a later step (e.g. the approval review step).
   */
  requireEnvironmentConfirmation?: boolean;
};

export function SaveExperimentModal({
  onCancel,
  onConfirm,
  isPending,
  hasStratifiedSampling = false,
  description,
  confirmLabel = 'Save changes and start new iteration',
  stepIndicator,
  initialReason,
  hasPendingApproval,
  experimentCurrentIterationStatus,
  requireEnvironmentConfirmation = true,
}: SaveExperimentModalProps) {
  const [reason, setReason] = useState(initialReason ?? '');
  const [isEnvironmentConfirmed, setIsEnvironmentConfirmed] = useState(false);

  const isConfirmDisabled = !reason.trim() || isPending || (requireEnvironmentConfirmation && !isEnvironmentConfirmed);

  const handleConfirm = () => {
    if (isConfirmDisabled) {
      return;
    }
    onConfirm({ reason });
  };
  return (
    <>
      <div slot="header">
        <Heading slot="title" aria-label="Save changes and start new iteration">
          Save changes and start new iteration
        </Heading>
        <Text slot="subtitle">Saving changes will automatically start a new iteration</Text>
        <div slot="close">
          <IconButton aria-label="close modal" icon="cancel" variant="minimal" onPress={onCancel} />
        </div>
      </div>
      <div slot="body" className={styles.body}>
        {description && <p>{description}</p>}
        {isExperimentApprovalsEnabled() && (
          <ExperimentPendingApprovalAlerts
            hasPendingApproval={hasPendingApproval}
            experimentCurrentIterationStatus={experimentCurrentIterationStatus}
          />
        )}
        {hasStratifiedSampling && isPending && (
          <Alert variant="inline" status="info" className={styles.stratifiedSamplingAlert}>
            We're setting up stratified sampling to make sure your experiment groups are well balanced — this can take a
            bit longer than usual.
          </Alert>
        )}
        <div className={styles.formSection}>
          <Label htmlFor="reason-textarea" className={styles.reasonLabel}>
            Reason for changes
            <RequiredAsterisk />
          </Label>
          <TextArea
            id="reason-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter your reason for making these changes"
            aria-required="true"
            rows={3}
          />
        </div>
        {requireEnvironmentConfirmation && (
          <div className={styles.formSection}>
            <ExperimentEnvironmentConfirmation onValidityChange={setIsEnvironmentConfirmed} />
          </div>
        )}
      </div>
      <div slot="footer" className={footerStyles.footer}>
        {stepIndicator && <span className={footerStyles.stepIndicator}>{stepIndicator}</span>}
        <div className={footerStyles.footerActions}>
          <Button variant="default" onClick={onCancel} isDisabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} isDisabled={isConfirmDisabled} isPending={isPending}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </>
  );
}
